import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import parser from 'html-react-parser';
import { withGoogleReCaptcha } from 'react-google-recaptcha-v3';

import { translate } from '../Services/translation';
import { isPhoneNumberValid } from '../Helpers/isPhoneNumberValid';

const CLASS_PREFIX = 'expert-book-form';

const ExpertBookForm = (props) => {
  const { data, googleReCaptchaProps } = props;
  const { executeRecaptcha } = googleReCaptchaProps;

  const { Description, PreferredContactMethod, Subject, Title } = data || {};

  const [requestLoading, setRequestLoading] = useState(false);
  const [requestSuccess, setRequestSuccess] = useState(false);
  const [requestError, setRequestError] = useState(false);
  const [telInputReady, setTelInputReady] = useState(false);

  const telInputRef = useRef();

  const showFDBK = requestLoading || requestSuccess || requestError;
  const showForm = !requestSuccess && !requestError;
  const buttonDisabled = requestLoading;

  const shouldTryAgain = requestError && !requestLoading;
  const shouldResetForm = requestSuccess && !requestLoading;

  const tryAgainLabel = translate('expertBookForm.action.tryAgain');
  const resetFormLabel = translate('expertBookForm.action.resetForm');
  const submitLabel = translate('expertBookForm.action.submit');

  const onSubmit = (e) => {
    e.preventDefault();
    const elements = e.target.elements;

    if (!executeRecaptcha) {
      console.error('Execute recaptcha not yet available');
      return;
    }

    const headers = new Headers();
    headers.append('Content-Type', `application/json`);
    headers.append(
      'litium-request-context',
      `{'ChannelSystemId':'${window.__litium.requestContext.channelSystemId}'}`
    );

    executeRecaptcha('expertBookForm').then((reCaptchaToken) => {
      setRequestLoading(true);
      if (elements) {
        const data = {};
        for (let i = 0; i < elements.length; i++) {
          const element = elements[i];
          data[element.name] = element.value;
        }

        fetch('/api/expertBooking/sendrequest', {
          method: 'POST',
          body: JSON.stringify({ ...data, reCaptchaToken }),
          headers: headers,
          redirect: 'follow',
        })
          .then((response) => {
            if (response.status === 200) {
              setRequestSuccess(true);
            } else {
              setRequestError(true);
            }
          })
          .catch((error) => {
            setRequestError(true);
            console.error('Error:', error);
          })
          .finally(() => {
            setRequestLoading(false);
          });
      }
    });
  };

  // Handle Phone input validation
  const onTelInput = (e) => {
    e.target.setCustomValidity('');
    const isValid = isPhoneNumberValid(e.target.value);
    if (!isValid) {
      e.target.setCustomValidity(
        translate('expertBookForm.action.phoneValidation')
      );
      e.target.reportValidity();
    }
  };

  useEffect(() => {
    if (telInputReady) {
      telInputRef.current.addEventListener('input', onTelInput);
    }

    return () => {
      if (telInputRef.current) {
        telInputRef.current.removeEventListener('input', onTelInput);
      }
    };
  }, [telInputReady]);

  const ButtonLabel = (
    <>
      {buttonDisabled && (
        <span className="spinner">{translate('general.loading')}</span>
      )}
      {shouldTryAgain ? tryAgainLabel : null}
      {shouldResetForm ? resetFormLabel : null}
      {!buttonDisabled && !shouldTryAgain && !shouldResetForm
        ? submitLabel
        : null}
    </>
  );

  /**
   * Reset form state.
   * Used for "Try again" and "Reset form" buttons.
   */
  const resetForm = () => {
    setRequestSuccess(false);
    setRequestError(false);
  };

  return (
    <>
      <div
        className={`${CLASS_PREFIX}__fdbk-state${
          showFDBK ? ` ${CLASS_PREFIX}__fdbk-state--visible` : ''
        } padding-horizontal-2 padding-vertical-3`}
      >
        {requestLoading ? (
          <span className={`${CLASS_PREFIX}__fdbk-state--loading`}>
            {translate('general.loading')}
          </span>
        ) : null}
        {requestSuccess ? (
          <span className={`${CLASS_PREFIX}__fdbk-state--success`}>
            {translate('expertBookForm.message.success')}
          </span>
        ) : null}
        {requestError ? (
          <span className={`${CLASS_PREFIX}__fdbk-state--error`}>
            {translate('expertBookForm.message.error')}
          </span>
        ) : null}
      </div>

      <form
        onSubmit={onSubmit}
        className={`${CLASS_PREFIX}__form row validate`}
        name={CLASS_PREFIX}
      >
        {showForm ? (
          <>
            <div className={`${CLASS_PREFIX}__form__fieldset`}>
              <label
                htmlFor="Name"
                className={`${CLASS_PREFIX}__form__label form__label`}
              >
                {translate('expertBookForm.label.name')}
              </label>
              <input
                name="Name"
                placeholder={translate('expertBookForm.placeholder.name')}
                className={`${CLASS_PREFIX}__form__input form__input`}
                required
                type="text"
              />
            </div>

            <div className={`${CLASS_PREFIX}__input-wrapper`}>
              <label
                htmlFor="Email"
                className={`${CLASS_PREFIX}__form__label form__label`}
              >
                {translate('expertBookForm.label.email')}
              </label>
              <input
                name="Email"
                placeholder={translate('expertBookForm.placeholder.email')}
                className={`${CLASS_PREFIX}__form__input form__input`}
                required
                type="email"
              />
            </div>

            <div className={`${CLASS_PREFIX}__input-wrapper`}>
              <label
                htmlFor="Phone"
                className={`${CLASS_PREFIX}__form__label form__label`}
              >
                {translate('expertBookForm.label.phone')}
              </label>
              <input
                name="Phone"
                placeholder={translate('expertBookForm.placeholder.phone')}
                className={`${CLASS_PREFIX}__form__input form__input`}
                required
                type="tel"
                ref={(node) => {
                  if (node) {
                    telInputRef.current = node;
                    setTelInputReady(true);
                  }
                }}
              />
            </div>

            <div className={`${CLASS_PREFIX}__input-wrapper`}>
              <label
                htmlFor="PreferredContactMethod"
                className={`${CLASS_PREFIX}__form__label form__label`}
              >
                {translate('expertBookForm.label.preferredContactMethod')}
              </label>
              <select
                name="PreferredContactMethod"
                className={`${CLASS_PREFIX}__form__input form__select`}
                required
              >
                <option value="" hidden>
                  {translate('expertBookForm.select.placeholder')}
                </option>
                {PreferredContactMethod.map((item, index) => (
                  <option key={index} value={item.Value}>
                    {item.Value}
                  </option>
                ))}
              </select>
            </div>

            <div className={`${CLASS_PREFIX}__input-wrapper`}>
              <label
                htmlFor="Subject"
                className={`${CLASS_PREFIX}__form__label form__label`}
              >
                {translate('expertBookForm.label.subject')}
              </label>
              <select
                name="Subject"
                className={`${CLASS_PREFIX}__form__input form__select`}
                required
              >
                <option value="" hidden>
                  {translate('expertBookForm.select.placeholder')}
                </option>
                {Subject.map((item, index) => (
                  <option key={index} value={item.Value}>
                    {item.Value}
                  </option>
                ))}
              </select>
            </div>

            <div className={`${CLASS_PREFIX}__input-wrapper`}>
              <label
                htmlFor="Message"
                className={`${CLASS_PREFIX}__form__label form__label`}
              >
                {translate('expertBookForm.label.message')}
              </label>
              <textarea
                name="Message"
                placeholder={translate('expertBookForm.placeholder.message')}
                className={`${CLASS_PREFIX}__form__textarea form__input`}
              />
            </div>
          </>
        ) : null}
        <div className={`${CLASS_PREFIX}__input-wrapper`}>
          <button
            type={showForm ? 'submit' : 'button'}
            className="button"
            disabled={buttonDisabled}
            onClick={showForm ? undefined : () => resetForm()}
          >
            {ButtonLabel}
          </button>
        </div>
      </form>
    </>
  );
};

const keyPropType = PropTypes.shape({
  Key: PropTypes.string.isRequired,
  Value: PropTypes.string.isRequired,
});

const propTypes = {
  data: PropTypes.shape({
    Description: PropTypes.string,
    PreferredContactMethod: PropTypes.arrayOf(keyPropType),
    Subject: PropTypes.arrayOf(keyPropType),
    Title: PropTypes.string,
  }),
  googleReCaptchaProps: PropTypes.object,
};

ExpertBookForm.propTypes = propTypes;

ExpertBookForm.defaultProps = {
  data: {},
  googleReCaptchaProps: {},
};

export default withGoogleReCaptcha(ExpertBookForm);
